/**
 * apiClient — SPA token-acquisition layer for the 2FA API gate.
 *
 * When TENANT_2FA_REQUIRED is on (server-side), every /api/* call that touches
 * a gated handler needs a SECOND FACTOR beyond the cdx_session cookie. The two
 * accepted factors are:
 *   1. An Entra access token (Authorization: Bearer ...), used by every normal
 *      page through apiFetch().
 *   2. A short-lived print token (X-Print-Token), used ONLY by print pages
 *      (which run in a headless render context that cannot complete an
 *      interactive MSAL redirect). printFetch() carries this factor.
 *
 * The cdx_session cookie stays the authorization / allowlist source of truth on
 * the server. The bearer / print token only proves the second factor; it never
 * replaces the cookie identity. credentials:'include' is therefore preserved on
 * every request so the cookie always rides along.
 *
 * When the flag is OFF (today's behavior, and every tenant that has not opted
 * in), the server ignores the absent factor and the cookie alone authorizes the
 * call. Attaching a bearer when one happens to be available is harmless, so
 * apiFetch is safe to adopt everywhere unconditionally.
 */
import { msalInstance, ensureMsalInitialized, apiTokenRequest } from '@auth/index';

/**
 * Build-time gate for the client-side second factor (bearer + print token). The
 * SPA cannot read the server-only TENANT_2FA_REQUIRED flag, so the entire token
 * layer is opt-in at BUILD time via VITE_API_BEARER_ENABLED. While it is not
 * 'true' (today, and every ship-dark deploy) the client NEVER mints or attaches
 * a second factor and behaves byte-identically to the legacy cookie-only client:
 * no /api/print/grant POST, no Authorization header, legacy noopener openers.
 */
function bearerEnabled(): boolean {
  return import.meta.env.VITE_API_BEARER_ENABLED === 'true';
}

/**
 * Acquire an Entra access token for the Portal API scope (the bearer second
 * factor). Returns null whenever a token cannot be obtained, in which case
 * apiFetch proceeds cookie-only.
 *
 * SHIP-DARK GATE: the SPA cannot read the server-only TENANT_2FA_REQUIRED flag
 * (it has no VITE_ prefix), so the bearer is opt-in at BUILD time via
 * VITE_API_BEARER_ENABLED. Until that is 'true' (set by deploy.ps1 only AFTER
 * identity/expose-api-scope.ps1 has exposed the API scope), this returns null
 * and apiFetch is a pure cookie-only passthrough that behaves byte-identically
 * to the legacy client.
 *
 * NEVER redirect and NEVER throw: a missing/unconsented scope or a transient
 * interaction-required must not bounce a page into a full-page Entra navigation
 * or reject every data call. We degrade to cookie-only. (When the factor flag is
 * ON, a 401-driven re-auth handler is the rollout item that covers genuine
 * interaction-required cases.)
 */
/**
 * Resolve the MSAL account that matches the cdx_session identity, so the bearer
 * is minted for the SAME user the cookie represents. Falls back to the active
 * account, then the first cached account. This prevents getAllAccounts()[0] from
 * picking a foreign / second cached account whose email/tid would fail the
 * server's second-factor match (a per-user lockout under flag-on).
 */
function getCdxAccount() {
  try {
    const email = (window as unknown as { CDX_IDENTITY?: { email?: string } }).CDX_IDENTITY?.email;
    if (email && typeof msalInstance.getAccountByUsername === 'function') {
      const byName = msalInstance.getAccountByUsername(email);
      if (byName) return byName;
    }
  } catch { /* ignore */ }
  return msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? null;
}

export async function getApiToken(): Promise<string | null> {
  if (!bearerEnabled()) return null;

  try {
    await ensureMsalInitialized();
    const account = getCdxAccount();
    if (!account) return null;
    const result = await msalInstance.acquireTokenSilent({ ...apiTokenRequest, account });
    return result.accessToken || null;
  } catch {
    // Silent acquisition failed (interaction_required, blocked silent iframe,
    // expired SSO). Degrade to cookie-only HERE and never redirect from
    // getApiToken: a redirect would change flag-off behavior and could fire
    // inside a print tab. Interactive recovery is driven by the 401 interceptor
    // in apiFetch, which only triggers when the server actually REQUIRES and
    // REJECTS the missing factor (i.e. the flag is on).
    return null;
  }
}

/** Merge a header value into whatever shape the caller passed in init.headers. */
function withHeader(init: RequestInit | undefined, name: string, value: string): Headers {
  const headers = new Headers(init?.headers);
  headers.set(name, value);
  return headers;
}

/** True when running inside a /print/* render tab (which has no MSAL session). */
export function isPrintContext(): boolean {
  try { return window.location.pathname.startsWith('/print/'); } catch { return false; }
}

/**
 * apiFetch — the standard fetch wrapper for ALL /api/* calls.
 *
 * In a normal tab it attaches Authorization: Bearer when a token is available.
 * In a /print/* render tab (no MSAL session) it transparently routes through
 * printFetch, so shared hooks that call apiFetch (useCampaignPerformance,
 * useSetupCampaigns, ...) automatically carry the X-Print-Token second factor
 * when they run inside a print page. credentials:'include' is always preserved
 * (the cdx_session cookie is the authorization source); caller init/headers are
 * merged without clobbering.
 */
const REAUTH_GUARD_KEY = 'cdx_api_reauth_attempted';
let reauthInFlight = false;

/* -------------------------------------------------------------------------- *
 * Visible auth-error state (Diamond Spec Rev4 D9): when the ONE permitted
 * interactive re-auth has already been spent and a gated call 401s again, the
 * user gets an explicit error surface instead of another redirect (and instead
 * of a silent dead page). useSyncExternalStore-compatible, mirroring the
 * download-token store below.
 * -------------------------------------------------------------------------- */
let authErrorState = false;
const authErrorListeners = new Set<() => void>();
function setAuthError(next: boolean): void {
  if (next === authErrorState) return;
  authErrorState = next;
  for (const l of authErrorListeners) l();
}
export function subscribeAuthError(listener: () => void): () => void {
  authErrorListeners.add(listener);
  return () => { authErrorListeners.delete(listener); };
}
export function getAuthErrorSnapshot(): boolean {
  return authErrorState;
}

/**
 * Named explicit reset for the bounded reauth (D9: "named explicit reset, no
 * auto-reset on res.ok"). Re-arms the single interactive re-auth and clears the
 * visible error. Called only from the error surface's Try-again action.
 */
export function resetReauthGuard(): void {
  try { sessionStorage.removeItem(REAUTH_GUARD_KEY); } catch { /* ignore */ }
  setAuthError(false);
}

/**
 * Bounded interactive re-auth when a gated /api call 401s under flag-on
 * (Diamond Spec Rev4 D9: exactly ONE redirect per session/page-load). The
 * sessionStorage guard is NEVER auto-cleared - not on res.ok, not on reload -
 * so a 401 that a fresh token cannot fix (e.g. a persistent email mismatch)
 * lands on the visible error surface on its SECOND occurrence instead of
 * redirect-looping. Only resetReauthGuard() (the user's Try-again) re-arms it.
 * Never runs in a print tab (apiFetch routes those to printFetch first) and
 * never when the bearer is disabled (flag-off keeps legacy 401 handling).
 */
async function reauthOnce(): Promise<void> {
  if (reauthInFlight) return;
  let alreadySpent = false;
  try { alreadySpent = sessionStorage.getItem(REAUTH_GUARD_KEY) === '1'; } catch { /* ignore */ }
  if (alreadySpent) {
    // Second 401 after the one permitted redirect: visible error, no redirect.
    setAuthError(true);
    return;
  }
  reauthInFlight = true;
  try {
    await ensureMsalInitialized();
    const account = getCdxAccount();
    if (!account) {
      // Pre-login context (fresh tab, MSAL cache is per-tab): there is no
      // account to recover with, the login wall owns sign-in from here.
      // Do NOT spend the one-shot and do NOT show the error banner - a 401
      // before sign-in is the expected state, not a failure (review finding:
      // the warm grant on /login must never burn the permit).
      return;
    }
    // A 401 can mean a missing/rejected BEARER (a fresh token fixes it) or an
    // expired/missing COOKIE (no Entra redirect can fix that - only re-opening
    // the signed ?t= link can). Probe the cookie-only /api/auth/link-me before
    // spending the single redirect so a dead cookie lands on the visible error
    // instead of a wasted full-page Entra round-trip.
    try {
      const me = await fetch('/api/auth/link-me', { credentials: 'include' });
      if (!me.ok) {
        setAuthError(true);
        return;
      }
    } catch { /* probe unreachable: fall through to the redirect attempt */ }
    try { sessionStorage.setItem(REAUTH_GUARD_KEY, '1'); } catch { /* ignore */ }
    await msalInstance.acquireTokenRedirect({ ...apiTokenRequest, account });
  } catch {
    // Redirect could not start; make the failure visible instead of silent.
    setAuthError(true);
  } finally {
    reauthInFlight = false;
  }
}

export async function apiFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
  opts?: { skipReauth?: boolean },
): Promise<Response> {
  if (isPrintContext()) return printFetch(input, init);

  const token = await getApiToken();
  // The Entra access token rides in the X-Cdx-Bearer CUSTOM header, not the
  // standard Authorization header: Azure Static Web Apps reserves Authorization
  // for its own platform token on the managed-functions route and replaces the
  // client's value with an HS256 SWA session token before it reaches the
  // Function (verified in prod 2026-06-12). A custom header is forwarded intact.
  const headers = token
    ? withHeader(init, 'X-Cdx-Bearer', token)
    : new Headers(init?.headers);

  const res = await fetch(input, { ...init, headers, credentials: 'include' });

  if (bearerEnabled() && res.status === 401 && !opts?.skipReauth) {
    // Server required and rejected the second factor: recover interactively,
    // bounded at exactly one redirect per session (D9). NO auto-reset on
    // res.ok - only the visible error surface's Try-again re-arms the guard.
    void reauthOnce();
  }
  return res;
}

/**
 * Cached print token for this page render.
 */
let printTokenCache: string | null = null;

let openerSevered = false;
/**
 * Reverse-tabnabbing hardening for print tabs. A print tab is opened with a
 * RETAINED handle (the opener navigates it after minting ?pt=), so it cannot be
 * opened with 'noopener'. Instead the print tab severs its own back-reference to
 * the opener from the child side. Same-origin and first-party, so this is
 * defense-in-depth. Idempotent and cheap; only runs inside a /print/* tab.
 */
function severOpener(): void {
  if (openerSevered) return;
  openerSevered = true;
  try { if (window.opener) window.opener = null; } catch { /* sealed / cross-origin: ignore */ }
}

/** Read a print token passed by the opener on the ?pt= URL param. */
function printTokenFromUrl(): string | null {
  if (isPrintContext()) severOpener();
  try {
    const pt = new URLSearchParams(window.location.search).get('pt');
    return pt && pt.length > 0 ? pt : null;
  } catch {
    return null;
  }
}

/** Mint a fresh print token via the bearer-gated grant (needs an MSAL session). */
async function mintPrintTokenViaGrant(): Promise<string | null> {
  // SHIP-DARK: with the bearer factor disabled there is no second factor to mint,
  // so never POST /api/print/grant. This is what keeps openers byte-identical to
  // legacy (zero network on a print/export click) when the flag is off.
  if (!bearerEnabled()) return null;
  try {
    // skipReauth: the grant is an OPTIONAL warm (download/print tokens). Its
    // 401s (pre-login tabs, transient mint failures) must never consume the
    // data path's single interactive re-auth or raise the error banner.
    const res = await apiFetch('/api/print/grant', { method: 'POST' }, { skipReauth: true });
    if (!res.ok) return null;
    const body = (await res.json()) as { token?: string };
    return body.token ?? null;
  } catch {
    return null;
  }
}

/**
 * getPrintToken — resolve the print second factor for THIS context.
 *
 * A print tab is window.open'd and has NO MSAL session, so it cannot mint its
 * own token. The opener (which DOES hold a bearer) mints one and passes it on
 * the ?pt= URL param; the print tab reads it here. As a fallback for contexts
 * that DO have a session (e.g. a same-tab preview), we self-mint via the
 * bearer-gated grant. Returns null where neither is possible (flag-off contexts
 * still work cookie-only).
 */
export async function getPrintToken(): Promise<string | null> {
  const fromUrl = printTokenFromUrl();
  if (fromUrl) { printTokenCache = fromUrl; return fromUrl; }
  if (printTokenCache) return printTokenCache;
  // A print tab has no MSAL session and cannot self-mint; returning null here
  // (rather than minting) also prevents an apiFetch -> printFetch -> getPrintToken
  // -> mint -> apiFetch recursion in a print context. Real print tabs receive
  // ?pt= from their opener (openPrintTab / appendPrintToken).
  if (isPrintContext()) return null;
  printTokenCache = await mintPrintTokenViaGrant();
  return printTokenCache;
}

/**
 * appendPrintToken — for OPENERS (a tab that HAS an MSAL session). Mints a print
 * token and appends it to a /print/* URL as ?pt=, so the opened print tab can
 * present the second factor without its own session. Returns the URL unchanged
 * when no token can be minted, so flag-off contexts still open cookie-only.
 */
export async function appendPrintToken(printPath: string): Promise<string> {
  const token = await mintPrintTokenViaGrant();
  if (!token) return printPath;
  const sep = printPath.includes('?') ? '&' : '?';
  return `${printPath}${sep}pt=${encodeURIComponent(token)}`;
}

/* -------------------------------------------------------------------------- *
 * Download token for GET-navigation assets (img/audio src, download anchors to
 * /api/setup/file, /api/db/audit/conflicts?csv, ...). A browser navigation
 * cannot send a custom header, so these carry the print/download token as a
 * ?pt= query param (the server accepts it for GETs, jti-bound to the cookie).
 * The token is warmed into a module cache so asset URLs can append it
 * SYNCHRONOUSLY at render time.
 * -------------------------------------------------------------------------- */
let downloadTokenCache: string | null = null;
const downloadTokenListeners = new Set<() => void>();
function setDownloadToken(next: string | null): void {
  if (next === downloadTokenCache) return;
  downloadTokenCache = next;
  for (const l of downloadTokenListeners) l();
}

/**
 * subscribeDownloadToken — register a listener fired whenever the download token
 * changes (warms or rotates). useSyncExternalStore-compatible; returns an
 * unsubscribe. Lets a component re-render so its assetUrl()-built href/src
 * refreshes when the token first lands or rotates (never serving an expired pt).
 */
export function subscribeDownloadToken(listener: () => void): () => void {
  downloadTokenListeners.add(listener);
  return () => { downloadTokenListeners.delete(listener); };
}

/** Current download token (or null). Stable snapshot for useSyncExternalStore. */
export function getDownloadTokenSnapshot(): string | null {
  return downloadTokenCache;
}

/**
 * warmDownloadToken — mint and cache a session download token, notifying any
 * subscribed components so their asset URLs refresh. No-op when the bearer is
 * disabled (flag-off, byte-identical legacy) or in a print tab (which forwards
 * its own ?pt=). Call on app mount and on an interval shorter than the 5-minute
 * token TTL so a baked-in token never expires while a page stays mounted.
 */
let warmRetryTimer: ReturnType<typeof setTimeout> | null = null;
let warmRetryCount = 0;
const WARM_MAX_RETRIES = 3;
const WARM_RETRY_MS = 4000;

export async function warmDownloadToken(): Promise<void> {
  if (!bearerEnabled() || isPrintContext()) {
    setDownloadToken(null);
    return;
  }
  const token = await mintPrintTokenViaGrant();
  if (token) {
    warmRetryCount = 0;
    if (warmRetryTimer) { clearTimeout(warmRetryTimer); warmRetryTimer = null; }
    setDownloadToken(token);
    return;
  }
  // Transient failure (the MSAL account may not be warm yet on a cold load, or a
  // brief silent-renewal hiccup): retry a few times on a short backoff so asset
  // ?pt= URLs recover within seconds instead of waiting the full 4-minute poll.
  if (warmRetryCount < WARM_MAX_RETRIES) {
    warmRetryCount += 1;
    if (warmRetryTimer) clearTimeout(warmRetryTimer);
    warmRetryTimer = setTimeout(() => { warmRetryTimer = null; void warmDownloadToken(); }, WARM_RETRY_MS);
  }
}

/**
 * assetUrl — append the download second factor to a same-origin /api asset URL
 * used by a browser navigation (image/audio src, download anchor href). Returns
 * the URL UNCHANGED when no token is available (flag-off = cookie-only, exactly
 * today's behavior). In a print tab it forwards the tab's own ?pt= so assets
 * render under the print factor too.
 */
export function assetUrl(url: string): string {
  const token = isPrintContext() ? printTokenFromUrl() : downloadTokenCache;
  if (!token) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}pt=${encodeURIComponent(token)}`;
}

/**
 * openPrintTab — convenience for openers (a tab WITH an MSAL session).
 *
 * SHIP-DARK (flag off): there is no second factor to mint, so this is EXACTLY
 * the legacy opener — window.open(path, '_blank', 'noopener,noreferrer'). No
 * /api/print/grant POST and no retained opener handle, byte-identical to base.
 *
 * Flag on: we must retain the handle to navigate the tab after the async mint,
 * so we open about:blank (same-origin) SYNCHRONOUSLY inside the user gesture (so
 * popup blockers allow it), then navigate it to the print URL with a freshly
 * minted ?pt= once the mint resolves. The opened print tab severs its own
 * window.opener on load (see severOpener) to restore tabnabbing hardening.
 * Synchronous (returns void) so call sites can use it directly in an onClick.
 */
export function openPrintTab(printPath: string): void {
  if (!bearerEnabled()) {
    window.open(printPath, '_blank', 'noopener,noreferrer');
    return;
  }
  // If the popup was blocked (win === null) we cannot recover from the async
  // continuation (the user gesture is gone), so we do not retry here; the user
  // can re-click with popups allowed.
  const win = window.open('about:blank', '_blank');
  void appendPrintToken(printPath).then((url) => {
    if (win && !win.closed) win.location.href = url;
  });
}

/**
 * printFetch — fetch wrapper used ONLY by print pages.
 *
 * Attaches X-Print-Token (the print second factor) instead of a bearer, keeps
 * credentials:'include' so the cdx_session cookie rides along, and merges any
 * caller init / headers. Print render contexts cannot complete an interactive
 * MSAL redirect, so they rely on a token minted earlier by an MFA'd session.
 */
export async function printFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const token = await getPrintToken();
  const headers = token
    ? withHeader(init, 'X-Print-Token', token)
    : new Headers(init?.headers);

  return fetch(input, {
    ...init,
    headers,
    credentials: 'include',
  });
}
